import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { Popover } from 'tio-ui/components/popover';
import { Listbox } from 'tio-ui/components/collections';
import { Select } from 'tio-ui/components/forms';
import { PhDotsThree } from 'ember-phosphor-icons';
import { modifier } from 'ember-modifier';
import { eq } from 'tio-ui/utilities';
import type RouterService from '@ember/routing/router-service';

export interface TabItem {
  label: string;
  route: string;
  model?: unknown;
}

interface NavTabsSignature {
  Args: {
    tabs: TabItem[];
  };
  Element: HTMLElement;
}

const navTabsItemBaseClasses = [
  'whitespace-nowrap',
  'border-b',
  'border-b-2',
  'px-1',
  'py-4',
  'text-sm',
  'font-medium',
];

const navTabsItemDefaultClasses = [
  'border-transparent',
  'text-gray-500',
  'dark:text-gray-400',
  'hover:border-gray-300',
  'dark:hover:border-gray-600',
  'hover:text-gray-700',
  'dark:hover:text-gray-300',
];

const navTabsItemActiveClasses = [
  'active',
  'border-indigo-500',
  'dark:border-indigo-400',
  'text-indigo-600',
  'dark:text-indigo-400',
];

class NavTabsComponent extends Component<NavTabsSignature> {
  @service declare router: RouterService;
  @tracked visibleCount = 0;
  @tracked hasMeasured = false;
  tabWidths: number[] = [];
  navElement?: HTMLElement;
  resizeObserver?: ResizeObserver;

  setupNav = modifier((element: HTMLElement) => {
    this.navElement = element;

    // Use requestAnimationFrame to ensure DOM is painted before measuring
    requestAnimationFrame(() => {
      this.measureAndCalculate();
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.calculateVisibleTabs();
    });
    this.resizeObserver.observe(element);

    return () => {
      this.resizeObserver?.disconnect();
    };
  });

  measureAndCalculate = () => {
    if (!this.navElement) return;

    // Measure all tab elements
    const tabElements = this.navElement.querySelectorAll<HTMLElement>('[data-nav-tab]');
    this.tabWidths = Array.from(tabElements).map((el) => {
      const style = window.getComputedStyle(el);
      const marginLeft = Number.parseFloat(style.marginLeft) || 0;
      const marginRight = Number.parseFloat(style.marginRight) || 0;
      return el.offsetWidth + marginLeft + marginRight;
    });

    this.hasMeasured = true;
    this.calculateVisibleTabs();
  };

  calculateVisibleTabs = () => {
    if (!this.navElement || !this.hasMeasured) return;

    const containerWidth = this.navElement.offsetWidth;
    const overflowButtonWidth = 60;
    const gapWidth = 32; // gap-x-8 = 2rem = 32px
    let availableWidth = containerWidth - overflowButtonWidth;
    let count = 0;

    for (let i = 0; i < this.tabWidths.length; i++) {
      const tabWidth = this.tabWidths[i] ?? 0;
      const gapNeeded = i > 0 ? gapWidth : 0;
      if (availableWidth >= tabWidth + gapNeeded) {
        availableWidth -= tabWidth + gapNeeded;
        count++;
      } else {
        break;
      }
    }

    // If all tabs fit, show them all (no overflow menu needed)
    const totalWidth = this.tabWidths.reduce((sum, w, i) => sum + w + (i > 0 ? gapWidth : 0), 0);
    if (totalWidth <= containerWidth) {
      count = this.args.tabs.length;
    }

    // Always show at least 1 tab
    if (count === 0 && this.args.tabs.length > 0) {
      count = 1;
    }

    this.visibleCount = count;
  };

  handleTabSelect = (key: string) => {
    const tab = this.args.tabs.find((t) => t.route === key);
    if (tab) {
      if (tab.model) {
        this.router.transitionTo(tab.route, tab.model);
      } else {
        this.router.transitionTo(tab.route);
      }
    }
  };

  get selectItems(): Array<{ key: string; value: string }> {
    return this.args.tabs.map((tab) => ({
      key: tab.route,
      value: tab.label,
    }));
  }

  get currentTab(): TabItem | undefined {
    return this.args.tabs.find((t) => t.route === this.router.currentRouteName);
  }

  get visibleTabs(): TabItem[] {
    // Before measurement, show all tabs so we can measure them
    if (!this.hasMeasured) {
      return this.args.tabs;
    }
    return this.args.tabs.slice(0, this.visibleCount);
  }

  get overflowTabs(): TabItem[] {
    if (!this.hasMeasured) {
      return [];
    }
    return this.args.tabs.slice(this.visibleCount);
  }

  get hasOverflow(): boolean {
    return this.hasMeasured && this.overflowTabs.length > 0;
  }

  get activeTabClass(): string {
    return [...navTabsItemBaseClasses, ...navTabsItemActiveClasses].join(' ');
  }

  get defaultTabClass(): string {
    return [...navTabsItemBaseClasses, ...navTabsItemDefaultClasses].join(' ');
  }

  <template>
    <div class="w-full" ...attributes>
      {{! Mobile select (sm and below) }}
      <div class="sm:hidden border-b border-gray-200 dark:border-gray-600 pb-4">
        <Select
          @items={{this.selectItems}}
          @selectedKey={{this.router.currentRouteName}}
          @onAction={{this.handleTabSelect}}
          @selectionMode="single"
        />
      </div>

      {{! Desktop tabs with overflow (sm and above) }}
      <div class="hidden sm:block border-b border-gray-200 dark:border-gray-600">
        <nav class="-mb-px flex items-center gap-x-8" aria-label="Tabs" {{this.setupNav}}>
          {{#each this.visibleTabs as |tab|}}
            {{#if tab.model}}
              <LinkTo
                @route={{tab.route}}
                @model={{tab.model}}
                class={{if
                  (eq tab.route this.router.currentRouteName)
                  this.activeTabClass
                  this.defaultTabClass
                }}
                data-nav-tab
              >
                {{tab.label}}
              </LinkTo>
            {{else}}
              <LinkTo
                @route={{tab.route}}
                class={{if
                  (eq tab.route this.router.currentRouteName)
                  this.activeTabClass
                  this.defaultTabClass
                }}
                data-nav-tab
              >
                {{tab.label}}
              </LinkTo>
            {{/if}}
          {{/each}}

          {{#if this.hasOverflow}}
            <Popover @placement="bottom-end" as |p|>
              <button
                type="button"
                class="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 -mb-0.5"
                {{p.trigger}}
                {{p.anchor}}
                aria-label="More tabs"
              >
                <PhDotsThree @size="20" />
              </button>
              <p.Content>
                <Listbox
                  @intent="primary"
                  @onAction={{this.handleTabSelect}}
                  @selectedKeys={{if this.currentTab (array this.currentTab.route) (array)}}
                  @selectionMode="single"
                  as |l|
                >
                  {{#each this.overflowTabs as |tab|}}
                    <l.Item @key={{tab.route}}>
                      {{tab.label}}
                    </l.Item>
                  {{/each}}
                </Listbox>
              </p.Content>
            </Popover>
          {{/if}}
        </nav>
      </div>
    </div>
  </template>
}

export default NavTabsComponent;
